<template>
  <div class="container-fluid mt-4 bg-white px-0">
    <chart-header
      :title="$t('flexigo.dashboard.efficiency')"
      :has-data="!!efficiencyItems.length"
      :show-group-by-toggle="true"
      @filter-change="onFilterChange"
      @download="download"
      @group-by-change="onGroupByChange"
    />
    <hr />
    <div class="px-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="chart-left-title">{{ $t('flexigo.dashboard.ridersPerHourPerVehicle') }}</div>
        <div class="chart-right-title">{{ $t('flexigo.dashboard.noOfferAvailable') }}</div>
      </div>
      <highcharts ref="efficiencyChart" :options="chartOptions"></highcharts>
    </div>
    <excel-downloader
      ref="excelDownloader"
      :data="excelData"
      :file-name="'Efficiency_Chart'"
      :sheet-name="'Efficiency'"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { format } from 'date-fns';
import { Chart } from 'highcharts-vue';
import {
  getXAxisFormatType,
  formatXAxisLabel,
  groupDataByFormatType,
  aggregateGroupedData,
  sortHourlyItemsByWindow,
  hourWindowComparator,
  calculateOperationHours,
} from '@/modules/Flexigo/helpers/chartXAxisFormatter';
import { exportChartAsPNG } from '@/modules/Flexigo/components/dashboard/otp/utils/chartExport';
import chartTooltipPositioner from '@/modules/Flexigo/helpers/chartTooltipPositioner';
import { isFutureOperationDay, lineSeriesWithoutPoint } from '@/modules/Flexigo/helpers/chartLineGaps';
import buildGapPlotLines from '@/modules/Flexigo/helpers/chartGapPlotLines';
import chartHeader from './ChartHeader.vue';
import ExcelDownloader from './ExcelDownloader.vue';

export default {
  name: 'EfficiencyChart',
  components: {
    chartHeader,
    highcharts: Chart,
    ExcelDownloader,
  },
  data() {
    return {
      selectedDate: 'thisweek',
      groupBy: 'date',
      filters: {
        startDate: null,
        endDate: null,
        fromHour: null,
        toHour: null,
      },
    };
  },
  computed: {
    ...mapGetters('onDemandDashboard', {
      efficiencyItems: 'efficiencyItems',
    }),
    xAxisFormatType() {
      return getXAxisFormatType(this.filters.startDate, this.filters.endDate, this.selectedDate, this.groupBy);
    },
    // Items as the chart/excel/gap-markers all see them: raw hourly rows for a single-day
    // 'date' view, grouped+aggregated otherwise (including 'timeOfDay' and 'weekday' which
    // always need grouping across days).
    processedItems() {
      let items = this.efficiencyItems || [];
      const formatType = this.xAxisFormatType;
      // Overnight operation windows wrap next-day early hours under the same operationDay;
      // reorder so 00:00 lands after 23:00 instead of before 20:00.
      items = sortHourlyItemsByWindow(items, formatType, this.filters.fromHour, this.filters.toHour);
      const needsGrouping = this.groupBy === 'timeOfDay' || this.groupBy === 'weekday' || formatType !== 'hourly';
      if (!needsGrouping || items.length === 0) return items;

      const grouped = groupDataByFormatType(items, formatType, this.groupBy);
      const compareHours = hourWindowComparator(this.filters.fromHour, this.filters.toHour);
      // Sort group keys for deterministic order (critical for timeOfDay/weekday).
      const sortedKeys = Object.keys(grouped).sort((a, b) => {
        if (this.groupBy === 'timeOfDay') return compareHours(a, b);
        if (this.groupBy === 'weekday') {
          const weekdayOrder = {
            Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7,
            Pazartesi: 1, Salı: 2, Çarşamba: 3, Perşembe: 4, Cuma: 5, Cumartesi: 6, Pazar: 7,
          };
          return (weekdayOrder[a] || 0) - (weekdayOrder[b] || 0);
        }
        return a.localeCompare(b);
      });
      return sortedKeys.map((key) =>
        aggregateGroupedData(grouped[key], ['ridersPerHourPerVehicle', 'noOfferAvailable'], []),
      );
    },
    // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip over
    // empty buckets. Skipped in weekday view: a "missing weekday" isn't meaningful on a
    // 7-slot categorical axis — the bars are intentionally per weekday name.
    gapPlotLines() {
      if (this.groupBy === 'weekday') return [];
      return buildGapPlotLines(this.processedItems, this.xAxisFormatType);
    },
    excelData() {
      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Determine column header based on groupBy
      let firstColumnHeader;
      if (this.groupBy === 'timeOfDay') {
        firstColumnHeader = this.$t('flexigo.dashboard.hour');
      } else if (this.groupBy === 'weekday') {
        firstColumnHeader = this.$t('flexigo.dashboard.weekday');
      } else {
        firstColumnHeader = this.$t('flexigo.dashboard.date');
      }

      // In multi-day selections with an operation window set, each bucket's
      // ridersPerHourPerVehicle is divided by the operation window length so the value is
      // normalized against the operating hours per day. Single-day selections pass through.
      const operationHours = calculateOperationHours(this.filters.fromHour, this.filters.toHour);
      const uniqueDays = new Set(
        (this.efficiencyItems || []).map((item) => item?.operationDay).filter((d) => d != null),
      );
      const divideByOperationHours = operationHours && operationHours > 0 && uniqueDays.size > 1;
      const rateFor = (item) => {
        const value = item?.ridersPerHourPerVehicle;
        if (value == null) return 0;
        return divideByOperationHours ? Number((value / operationHours).toFixed(2)) : value;
      };

      return items.map((item) => {
        const label = formatXAxisLabel(item, formatType, this.$i18n.locale);

        return {
          [firstColumnHeader]: label,
          [this.$t('flexigo.dashboard.riderPerHourPerVehicle')]: rateFor(item),
          [this.$t('flexigo.dashboard.noOfferAvailable')]: item?.noOfferAvailable || 0,
        };
      });
    },
    chartOptions() {
      const chartOptions = {
        chart: {
          backgroundColor: '#ffffff',
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: [],
          crosshair: true,
          // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip
          // over empty buckets (mirrors Shuttle RidershipByRouteChart). No-op on weekday
          // view since the categorical axis has no notion of a skipped slot.
          plotLines: this.gapPlotLines,
        },
        yAxis: [
          {
            title: { text: '' },
            min: 0,
            tickInterval: 1,
            gridLineColor: '#f0f2f5',
            plotLines: [
              {
                value: 6,
                color: '#5b8ff9',
                width: 1,
                dashStyle: 'Dash',
              },
            ],
          },
          {
            title: { text: '' },
            min: 0,
            tickInterval: 1,
            opposite: true,
            gridLineWidth: 0,
          },
        ],
        plotOptions: {
          column: {
            borderWidth: 0,
            pointWidth: 14,
            pointPlacement: 'on',
            color: '#65728a',
          },
          spline: {
            lineWidth: 2,
            color: '#5b8ff9',
          },
        },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: '#363647',
          style: { color: '#ffffff' },
          borderRadius: 6,
          positioner(labelWidth, labelHeight, point) {
            return chartTooltipPositioner(labelWidth, labelHeight, point, this.chart);
          },
          formatter() {
            let tooltipHtml = `<div class="dashboard-graph-tooltip"><div class="title">${this.x}</div>`;

            this.points.forEach((point) => {
              const seriesName = point.series.name;
              const value = point.y || 0;

              tooltipHtml += `
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <span class="text-label">${seriesName}</span>
                  </div>
                  <span class="text-value">${value}</span>
                </div>
              `;
            });

            // A null line point is left out of the shared tooltip; list it as N/A so the metric
            // doesn't silently disappear on buckets where it has no value.
            lineSeriesWithoutPoint(this.points).forEach((series) => {
              tooltipHtml += `
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <span class="text-label">${series.name}</span>
                  </div>
                  <span class="text-value">N/A</span>
                </div>
              `;
            });

            tooltipHtml += '</div>';
            return tooltipHtml;
          },
        },
        legend: {
          squareSymbol: true,
          symbolRadius: 0,
          useHTML: true,
          margin: 30,
          labelFormatter() {
            return `<div style="color: #687692; font-weight: normal;">${this.name}</div>`;
          },
        },
        series: [
          {
            name: this.$t('flexigo.dashboard.riderPerHourPerVehicle'),
            type: 'spline',
            yAxis: 0,

            data: [],
            marker: {
              fillColor: '#ffffff',
              lineWidth: 2,
              lineColor: '#5b8ff9',
              radius: 5,
              symbol: 'circle',
            },
          },
          {
            name: this.$t('flexigo.dashboard.noOfferAvailable'),
            type: 'column',
            yAxis: 0,
            data: [],
          },
        ],
      };

      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Format X-axis labels
      chartOptions.xAxis.categories = items.map((item) => formatXAxisLabel(item, formatType, this.$i18n.locale));

      // In multi-day selections with an operation window set, each bucket's
      // ridersPerHourPerVehicle is divided by the operation window length so the value is
      // normalized against the operating hours per day. Single-day selections pass through.
      const operationHours = calculateOperationHours(this.filters.fromHour, this.filters.toHour);
      const uniqueDays = new Set(
        (this.efficiencyItems || []).map((item) => item?.operationDay).filter((d) => d != null),
      );
      const divideByOperationHours = operationHours && operationHours > 0 && uniqueDays.size > 1;

      // Riders per hour per vehicle has no value — so the line breaks rather than drawing 0
      // (VSER-14215) — when the backend sends null for the bucket, or when the day is still
      // in the future on the date axis (weekday/time-of-day buckets span several days).
      // Note: the backend currently still returns 0.0 for hours with no active vehicle; that
      // case only becomes a gap once it sends null there.
      const hideFutureDays = this.groupBy === 'date';
      chartOptions.series[0].data = items.map((item) => {
        const value = item?.ridersPerHourPerVehicle;
        if (value == null || (hideFutureDays && isFutureOperationDay(item?.operationDay))) return null;
        const normalized = divideByOperationHours ? value / operationHours : value;
        return Number(normalized.toFixed(2));
      });
      chartOptions.series[1].data = items.map((item) => item?.noOfferAvailable || 0);

      return chartOptions;
    },
  },
  methods: {
    ...mapActions('onDemandDashboard', ['fetchEfficiencyData']),

    download(type) {
      if (type === 'image') {
        this.downloadChartAsImage();
        return;
      }
      this.$refs.excelDownloader.download();
    },
    downloadChartAsImage() {
      const chartInstance = this.$refs.efficiencyChart?.chart;

      exportChartAsPNG(chartInstance, `Efficiency_Chart_${format(new Date(), 'yyyy-MM-dd')}`, {
        width: 1200,
        height: 600,
        scale: 2,
        surface: 'efficiency',
      });
    },
    async onFilterChange({ startDate, endDate, fromHour, toHour, selectedDate }) {
      this.selectedDate = selectedDate;
      this.filters = { startDate, endDate, fromHour, toHour };

      await this.fetchEfficiencyData({
        startDate,
        endDate,
        fromHour,
        toHour,
      });
    },
    onGroupByChange(groupBy) {
      this.groupBy = groupBy;
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-left-title {
  color: #5287e3;
}
.chart-right-title {
  color: #363647;
}
</style>
